import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { VOICE_AGENTS_FAQS } from "../data/seo/faqs";
import { comOnlyInboundGuideLink, withInboundSetupGuideLink } from "../data/seo/inboundVoiceGuide";
import { buildServiceSchema } from "../data/seo/structuredData";
import { UsOutboundCallingNote, UsOutboundCallingSources, voiceAgentCitationsForBuild } from "../data/seo/voiceAgentCitations";
import { comMarketing, KW3_PHONE_BLOG_PATH, KW3_VOICE } from "../data/seo/kw3OnPage";

// One literal env compare. Vite folds it per build, so the India bundle
// never keeps the FCC module this gates.
const showUsCallingRules = import.meta.env.VITE_SITE_REGION !== "in";

const keyFeatures = [
    {
        title: "Customer Support",
        description: "Inbound voice agent that answers FAQs, looks up order or account info, and resolves Tier-1 tickets without queueing the caller.",
    },
    {
        title: "Appointment Booking",
        description: "Books, reschedules, and confirms appointments live on the call — Google or Microsoft calendar synced both ways.",
    },
    {
        title: "Lead Generation & Sales",
        description: "Qualifies inbound leads with custom scripts, scores them in real time, and fires SMS or webhook follow-ups instantly.",
    },
    {
        title: "Survey & Feedback",
        description: "Runs structured voice surveys inbound or outbound, captures answers as Data Collection records, and exports to your CRM.",
    },
    {
        title: "Multilingual Out of the Box",
        description: "English (US, UK, IN, AU, CA, IE, NZ), Hindi, Tamil, Telugu, Kannada, Malayalam, Marathi, Bengali, Gujarati, Punjabi.",
    },
    {
        title: "Smart Country Routing",
        description: "India routes through Jio + MSG91 (DLT-compliant). Every other market routes through a managed number or your own Twilio account. Picked automatically per widget.",
    },
    {
        title: "Outbound Triggers",
        description: "Webhooks, Shopify events, time-relative cadences, internal events, Data Collection watches and scheduled lists — all dial out automatically.",
    },
    {
        title: "SMS Reminders & Follow-ups",
        description: "Pre-call reminders, missed-call rescues, and post-call summaries delivered as SMS — no extra plumbing.",
    },
    {
        title: "Live Human Transfer",
        description: "Warm or cold transfer to a human agent the moment the conversation needs it, with the full call context handed over.",
    },
];

const pipelineNodes = [
    { name: "Greet", color: "from-emerald-500 to-teal-500" },
    { name: "Listen", color: "from-indigo-500 to-purple-500" },
    { name: "KB Lookup", color: "from-orange-500 to-pink-500" },
    { name: "Tool Call", color: "from-blue-500 to-cyan-500" },
    { name: "Book / Send SMS", color: "from-yellow-500 to-orange-500" },
    { name: "Transfer / End", color: "from-slate-500 to-slate-700" },
];

const triggerSources = [
    {
        title: "Webhook",
        description: "Any external system can POST a payload that fires a personalized outbound call.",
    },
    {
        title: "Shopify Event",
        description: "Order created, abandoned cart, refund issued — call the customer the moment it matters.",
    },
    {
        title: "Time-Relative",
        description: "Schedule calls relative to any field — e.g. 24 hours before an appointment, or 3 days after signup.",
    },
    {
        title: "Internal Event",
        description: "Trigger a callback whenever a workflow, support ticket or pipeline emits an internal event.",
    },
    {
        title: "Data Collection Watch",
        description: "When a row in a watched collection changes, the matching contact gets a call automatically.",
    },
    {
        title: "Scheduled List",
        description: "Upload a CSV or sync a list — the agent dials it on a cadence with retry and consent rules built in.",
    },
];

const useCases = [
    {
        title: "E-commerce Stores",
        description: "Recover abandoned carts, confirm high-value orders, and answer order-tracking calls 24/7.",
    },
    {
        title: "Clinics & Healthcare",
        description: "Book and confirm appointments, send reminders, route urgent calls to a human on call.",
    },
    {
        title: "Real Estate",
        description: "Qualify buyer and tenant inquiries, schedule viewings, and drip-call cold leads on autopilot.",
    },
    {
        title: "Local Services",
        description: "Never miss a call — quote requests, dispatch updates, and after-hours overflow are handled by the agent.",
    },
];

const primaryKeywords = [
    "AI voice agent",
    "AI call widget",
    "AI phone agent",
    "Outbound AI calls",
    "Inbound AI receptionist",
    "AI appointment booking",
    "AI call pipeline",
    "Voice automation",
];

interface CallTurn {
    speaker: "AI" | "Caller";
    text: string;
}

const callScript: CallTurn[] = [
    { speaker: "AI", text: "Thanks for calling Acme Dental. This is Aria — how can I help?" },
    { speaker: "Caller", text: "I'd like to book a cleaning, ideally Friday afternoon." },
    { speaker: "AI", text: "I have 2:30pm or 4:15pm Friday. Which works?" },
    { speaker: "Caller", text: "4:15 please." },
    { speaker: "AI", text: "Booked. You'll get a confirmation SMS in a few seconds. Anything else?" },
];

export default function CallWidgetLandingPage() {
    const [visibleTurns, setVisibleTurns] = useState(0);

    usePageSeo({
        title: comMarketing()
            ? KW3_VOICE.title
            : "AI Voice Agents & Call Pipelines | Inbound + Outbound Calls 24/7 | FloGPT",
        description:
            "AI voice agents that answer inbound calls, place outbound calls, book appointments, qualify leads, and run multilingual conversations 24/7. Build your call flow visually with FloGPT Call Pipelines.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-voice-agents",
    });

    const usCallingRules = useMemo(
        () => (showUsCallingRules ? voiceAgentCitationsForBuild() : null),
        [],
    );
    const inboundSetupGuide = comOnlyInboundGuideLink();
    const voiceFaqs = useMemo(() => withInboundSetupGuideLink(VOICE_AGENTS_FAQS), []);

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI Voice Agents",
                description:
                    "AI voice agents that answer inbound calls, place outbound calls, book appointments, qualify leads, and warm-transfer to humans. 16 languages including 9 Indic locales. Buy a number in-app or bring your own from Twilio, Jio or MSG91.",
                path: "/ai-voice-agents",
                serviceType: "AI Voice Agent / Conversational IVR",
                citations: usCallingRules?.map((source) => ({
                    name: source.name,
                    url: source.url,
                })),
                features: [
                    "24/7 inbound call answering",
                    "Outbound calls from webhooks, schedules, and Shopify events",
                    "Live appointment booking on the call (Google, Outlook)",
                    "Lead qualification with real-time scoring",
                    "16 languages, including 9 Indic languages",
                    "Smart carrier routing (managed numbers, Twilio, Jio, MSG91)",
                    "Warm and cold transfer to humans",
                    "Pre-call SMS reminders, post-call summaries",
                ],
            }),
        [usCallingRules],
    );

    useEffect(() => {
        if (visibleTurns >= callScript.length) {
            const reset = window.setTimeout(() => setVisibleTurns(0), 4000);
            return () => window.clearTimeout(reset);
        }
        const timer = window.setTimeout(() => {
            setVisibleTurns((n) => n + 1);
        }, visibleTurns === 0 ? 700 : 1700);
        return () => window.clearTimeout(timer);
    }, [visibleTurns]);

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                    Call Widget · Call Pipeline
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    AI Voice Agents That Answer & Place Calls{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        24/7
                                    </motion.span>
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    The FloGPT Call Widget puts a voice agent on your phone number that answers inbound calls and places outbound ones, on a call flow you design visually with no code.
                                    {showUsCallingRules && usCallingRules && (
                                        <UsOutboundCallingNote rules={usCallingRules} />
                                    )}
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Book a Free Demo
                                    </a>
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Start Building Free
                                    </Link>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· Inbound + outbound</span>
                                    <span>· 16+ languages</span>
                                    <span>· Managed numbers · Twilio · Jio · MSG91</span>
                                </div>
                            </motion.div>

                            {/* Animated call demo */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-orange-500/25 to-pink-500/25 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                                                A
                                            </div>
                                            <div>
                                                <div className="text-white font-semibold">Aria · AI Voice Agent</div>
                                                <div className="text-xs text-slate-400">Acme Dental · +1 (555) 010-2025</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-emerald-300">
                                            <motion.span
                                                className="h-2 w-2 rounded-full bg-emerald-400"
                                                animate={{ opacity: [0.4, 1, 0.4] }}
                                                transition={{ duration: 1.4, repeat: Infinity }}
                                            />
                                            Live call
                                        </div>
                                    </div>

                                    <div className="mt-6 space-y-3 min-h-[260px]">
                                        {callScript.slice(0, visibleTurns).map((turn, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className={`w-fit max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow ${
                                                    turn.speaker === "AI"
                                                        ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white"
                                                        : "bg-slate-800/80 text-slate-100"
                                                } ${turn.speaker === "Caller" ? "ml-auto" : ""}`}
                                            >
                                                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{turn.speaker}</div>
                                                {turn.text}
                                            </motion.div>
                                        ))}
                                    </div>

                                    <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4 text-xs text-slate-400">
                                        <span>Pipeline: Dental Booking v3</span>
                                        <span>00:42 · Avg latency 480ms</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Stop missing calls. Stop dialing one-by-one.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Every after-hours call gets answered, and every outbound list gets worked on the cadence you set.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                FloGPT picks up every inbound call instantly and dials every outbound list automatically — using the same agent, on the same pipeline you designed.
                            </p>
                            {inboundSetupGuide ? (
                                <p className="mt-4">
                                    <Link
                                        to={inboundSetupGuide.href}
                                        className="font-medium text-orange-400 hover:text-orange-300"
                                    >
                                        {inboundSetupGuide.label}
                                    </Link>
                                </p>
                            ) : null}
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Why this isn't another IVR
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Press-1 menus and scripted bots make callers hang up.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT's voice agent:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "Understands free-form speech in 16+ languages",
                                    "Looks up real data mid-call (Shopify, your DB, calendars, knowledge base)",
                                    "Books appointments, sends SMS, fires webhooks live on the call",
                                    "Transfers warm to a human the second it's needed",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">It sounds — and acts — like a trained agent.</p>
                        </article>
                    </motion.div>
                </section>

                {/* CALL PIPELINE SECTION */}
                <section id="call-pipeline" className="relative overflow-hidden">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
                        <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                                    Call Pipeline
                                </div>
                                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    Design your call flow visually. Publish in one click.
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed">
                                    Drag nodes to design exactly how the agent greets, listens, looks things up, takes actions, and ends or transfers. Start from a vertical pack — Dental, Real Estate, Retail, Support — or build from scratch.
                                </p>
                                <ul className="mt-8 grid grid-cols-2 gap-4 text-slate-300">
                                    {[
                                        "Vertical pack templates",
                                        "Branching & guards",
                                        "Tool calls (Shopify, KB, calendar, SMS)",
                                        "Versioning & rollback",
                                    ].map((t) => (
                                        <li key={t} className="flex items-center gap-2">
                                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8">
                                    <Link
                                        to="/ai-call-pipeline"
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        {comMarketing() ? "Call flow editor" : "Learn more about Call Pipeline"}
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                        </svg>
                                    </Link>
                                </div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                <div className="relative">
                                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 blur-xl" />
                                    <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                                        <div className="grid grid-cols-3 gap-4">
                                            {pipelineNodes.map((n, idx) => (
                                                <motion.div
                                                    key={n.name}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.05 * idx }}
                                                    viewport={{ once: true }}
                                                    className={`rounded-xl border border-slate-800 bg-gradient-to-r ${n.color} p-4 text-center text-sm font-semibold text-white shadow-lg`}
                                                >
                                                    {n.name}
                                                </motion.div>
                                            ))}
                                        </div>
                                        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 font-mono">
                                            {[
                                                "if (intent == 'book') -> node:CheckCalendar",
                                                "elif (intent == 'cancel') -> node:LookupAppointment",
                                                "elif (urgency > 0.7) -> node:TransferToHuman",
                                                "else -> node:AnswerFromKB",
                                            ].map((line, i) => (
                                                <div key={i} className="flex items-start gap-3">
                                                    <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                                                    <span className="whitespace-pre-wrap">{line}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything the Call Widget can do</h2>
                        <p className="mt-4 text-slate-300">
                            One agent, one pipeline — handling support, booking, sales, and surveys across inbound and outbound.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 to-pink-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 border border-orange-400/30">
                                        <svg className="h-5 w-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {comMarketing() ? (
                    <section className="mx-auto max-w-4xl px-6 lg:px-8 pt-16" aria-labelledby="outbound-ai-agent">
                        <h2 id="outbound-ai-agent" className="text-2xl sm:text-3xl font-bold text-white">{KW3_VOICE.h2}</h2>
                        {KW3_VOICE.sentences.map((sentence) => (
                            <p key={sentence} className="mt-4 text-lg leading-relaxed text-slate-300">{sentence}</p>
                        ))}
                        <p className="mt-4 text-lg leading-relaxed text-slate-300">
                            Set the rules under{" "}
                            <Link to="/ai-outbound-triggers" className="font-medium text-orange-400 hover:text-orange-300">
                                outbound triggers
                            </Link>
                            .
                        </p>
                    </section>
                ) : null}

                {/* OUTBOUND TRIGGERS */}
                <section id="outbound-triggers" className="relative overflow-hidden">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-orange-500/30 blur-3xl" />
                        <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <div className="text-center max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-4">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Outbound Triggers
                            </div>
                            <h2 className="text-3xl font-bold text-white">Six ways the agent dials out — automatically</h2>
                            <p className="mt-4 text-slate-300">
                                Plug your business events into the Call Widget and let the agent handle the call so your team doesn't have to.
                            </p>
                        </div>

                        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {triggerSources.map((source) => (
                                <motion.article
                                    key={source.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all"
                                >
                                    <h3 className="text-lg font-semibold text-white">{source.title}</h3>
                                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{source.description}</p>
                                </motion.article>
                            ))}
                        </div>
                        <div className="mt-12 text-center">
                            <Link
                                to="/ai-outbound-triggers"
                                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                            >
                                Learn more about Outbound Triggers
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* USE CASES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Built for teams that live and die on the phone</h2>
                        <p className="mt-4 text-slate-300">
                            From dental clinics to D2C stores — the same widget, tuned with a different pipeline.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {useCases.map((u, idx) => (
                            <motion.article
                                key={u.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20 hover:border-orange-400/30 transition-all"
                            >
                                <h3 className="text-xl font-semibold text-white">{u.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{u.description}</p>
                            </motion.article>
                        ))}
                    </div>
                    {comMarketing() ? (
                        <p className="mx-auto mt-10 max-w-3xl text-center text-slate-300">
                            The same agent runs as{" "}
                            <Link to="/use-cases/home-services" className="font-medium text-orange-400 hover:text-orange-300">
                                home services AI
                            </Link>{" "}
                            for HVAC and plumbing, as a{" "}
                            <Link to="/use-cases/restaurants" className="font-medium text-orange-400 hover:text-orange-300">
                                restaurant call bot
                            </Link>{" "}
                            for reservations, and the{" "}
                            <Link to={KW3_PHONE_BLOG_PATH} className="font-medium text-orange-400 hover:text-orange-300">
                                automated phone agent
                            </Link>{" "}
                            article walks through the line itself.
                        </p>
                    ) : null}
                </section>

                {showUsCallingRules && usCallingRules && <UsOutboundCallingSources rules={usCallingRules} />}

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">See your own call agent in action</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll wire up a live demo on a real number you can dial.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Voice Agent Demo"
                            subtitle="Tell us a bit about your use case and we'll have a working agent ready for the call"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>

                <FAQSection
                    id="voice-agents"
                    eyebrow="Voice Agents · FAQ"
                    heading="Common questions about FloGPT voice agents"
                    items={voiceFaqs}
                />

                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 pb-20">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 text-center">
                            <p className="text-sm text-slate-300">
                                Comparing voice-AI platforms? See how FloGPT stacks up against{" "}
                                <Link to="/compare/flogpt-vs-vapi" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">Vapi</Link>,{" "}
                                <Link to="/compare/flogpt-vs-bland" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">Bland</Link>,{" "}
                                <Link to="/compare/flogpt-vs-retell" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">Retell</Link>{" "}
                                and{" "}
                                <Link to="/compare/flogpt-vs-synthflow" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">Synthflow</Link>
                                {" "}— or browse{" "}
                                <Link to="/compare" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">all comparisons</Link>
                                {" "}or the{" "}
                                <Link to="/alternatives" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">alternatives hub</Link>.
                            </p>
                            <p className="mt-3 text-sm text-slate-300">
                                Ready to place calls, not just answer them? Start from a{" "}
                                <Link to="/sales-call-pipeline" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">pre-built sales call pipeline</Link>
                                {" "}or{" "}
                                <Link to="/ai-outbound-triggers" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">outbound triggers</Link>.
                            </p>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />

            <JsonLd id="service-voice-agents" data={serviceSchema} />
        </div>
    );
}
